import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import { styledLayout } from '@pkg/primitives';

import { tokens } from '../../../../tokens';

import { secondaryColumnTransition, SecondaryColumnVariant, secondaryColumnVariants } from './Columns.motion';

interface ColumnsWrapperStyleProps {
    $stretch?: boolean;
}

interface CommonColumnStyleProps {
    $padded?: boolean;
    $scrollable?: boolean;
}

const scrollableColumn = css`
    padding-top: ${tokens.spacing.tight}px;
    padding-bottom: ${tokens.spacing.tight}px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    ${styledLayout.scrollbar({
        thumbColor: 'var(--yat_scrollbar-thumb-color)',
        trackColor: 'transparent',
    })};

    ${styledLayout.featherMask({
        featherSize: tokens.spacing.tight,
        scrollbarSize: tokens.spacing.tight / 2,
    })};
`;

const primaryColumnPadding = css`
    padding-left: ${tokens.spacing.tight}px;
    padding-right: ${tokens.spacing.tight}px;

    // NOTE: The mix of 'margin-padding' is to get proper
    // alignment of the scrollbar, as well as spacing when
    //  the <Column.Secondary /> is closed.
    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding-left: ${tokens.spacing.loose}px;
        margin-right: ${tokens.spacing.tight}px;
    }
`;

const secondaryColumnPadding = css`
    padding-left: ${tokens.spacing.tight}px;
    padding-right: ${tokens.spacing.tight}px;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding-left: 0;
        padding-right: ${tokens.spacing.loose}px;
    }
`;

export const ColumnsWrapper = styled.div<ColumnsWrapperStyleProps>`
    ${styledLayout.fullHeightFix};
    display: grid;
    grid-template-columns: 1fr auto;
    flex: ${({ $stretch }) => ($stretch ? '1 1 auto' : '0 0 auto')};
`;

export const PrimaryWrapper = styled.div<CommonColumnStyleProps>`
    --yat_scrollbar-thumb-color: ${({ theme }) => theme.shades.white['20']};

    ${styledLayout.fullHeightFix};
    ${({ $scrollable }) => $scrollable && scrollableColumn};
    ${({ $padded }) => $padded && primaryColumnPadding};
`;

export const SecondaryWrapper = styled(motion.div).attrs({
    variants: secondaryColumnVariants,
    initial: SecondaryColumnVariant.Hidden,
    animate: SecondaryColumnVariant.Show,
    exit: SecondaryColumnVariant.Hidden,
    transition: secondaryColumnTransition,
})<CommonColumnStyleProps>`
    --yat_scrollbar-thumb-color: ${({ theme }) => theme.colors.purple};

    ${styledLayout.fullHeightFix};
    ${({ $scrollable }) => $scrollable && scrollableColumn};
    ${({ $padded }) => $padded && secondaryColumnPadding};

    width: ${tokens.layout.secondary.width}px;
`;
